/**
 * Système de cache avec IndexedDB
 * 
 * Permet de stocker les données localement pour :
 * - Améliorer les performances (moins de requêtes Firestore)
 * - Fonctionner hors-ligne
 * - Réduire les coûts Firebase
 */

const DB_NAME = 'ConakryEnPocheCache';
const DB_VERSION = 1;

// Stores IndexedDB
export const STORES = {
  BUSINESSES: 'businesses',
  EVENTS: 'events',
  REVIEWS: 'reviews',
  FAVORITES: 'favorites',
  CATEGORIES: 'categories',
  METADATA: 'metadata'
};

/**
 * Ouvrir la base de données IndexedDB
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Créer les stores si ils n'existent pas
      Object.values(STORES).forEach(storeName => {
        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, { keyPath: 'id' });
          // Index pour la recherche par userId, businessId, etc.
          if (storeName === STORES.FAVORITES) {
            store.createIndex('userId', 'userId', { unique: false });
          }
          if (storeName === STORES.REVIEWS) {
            store.createIndex('businessId', 'businessId', { unique: false });
          }
        }
      });
    };
  });
};

/**
 * Vérifier si IndexedDB est disponible
 */
export const isIndexedDBAvailable = () => {
  return typeof window !== 'undefined' && 'indexedDB' in window;
};

/**
 * Obtenir une valeur du cache
 * 
 * @param {string} storeName - Nom du store
 * @param {string} key - Clé de la valeur
 * @returns {Promise<any|null>} - La valeur ou null si non trouvée
 */
export const getFromCache = async (storeName, key) => {
  if (!isIndexedDBAvailable()) {
    return null;
  }

  try {
    const db = await openDB();
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result;
        // Vérifier si le cache n'est pas expiré
        if (result && result.expiresAt && new Date(result.expiresAt) > new Date()) {
          resolve(result.data);
        } else if (result && !result.expiresAt) {
          // Pas d'expiration définie
          resolve(result.data);
        } else {
          // Cache expiré
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('Erreur lors de la lecture du cache:', error);
    return null;
  }
};

/**
 * Mettre une valeur dans le cache
 * 
 * @param {string} storeName - Nom du store
 * @param {string} key - Clé de la valeur
 * @param {any} data - Données à mettre en cache
 * @param {number} ttl - Time to live en millisecondes (défaut: 1 heure)
 */
export const setToCache = async (storeName, key, data, ttl = 3600000) => {
  if (!isIndexedDBAvailable()) {
    return;
  }

  try {
    const db = await openDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const cacheEntry = {
      id: key,
      data,
      cachedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + ttl).toISOString()
    };

    await store.put(cacheEntry);
  } catch (error) {
    console.warn('Erreur lors de l\'écriture dans le cache:', error);
  }
};

/**
 * Mettre plusieurs valeurs dans le cache
 */
export const setMultipleToCache = async (storeName, items, ttl = 3600000) => {
  if (!isIndexedDBAvailable()) {
    return;
  }

  try {
    const db = await openDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);

    const promises = items.map(item => {
      const cacheEntry = {
        id: item.id,
        data: item,
        cachedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + ttl).toISOString()
      };
      return store.put(cacheEntry);
    });

    await Promise.all(promises);
  } catch (error) {
    console.warn('Erreur lors de l\'écriture multiple dans le cache:', error);
  }
};

/**
 * Obtenir toutes les valeurs d'un store
 */
export const getAllFromCache = async (storeName) => {
  if (!isIndexedDBAvailable()) {
    return [];
  }

  try {
    const db = await openDB();
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const results = request.result
          .filter(item => {
            // Filtrer les entrées expirées
            if (item.expiresAt) {
              return new Date(item.expiresAt) > new Date();
            }
            return true;
          })
          .map(item => item.data);
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('Erreur lors de la lecture du cache:', error);
    return [];
  }
};

/**
 * Supprimer une valeur du cache
 */
export const removeFromCache = async (storeName, key) => {
  if (!isIndexedDBAvailable()) {
    return;
  }

  try {
    const db = await openDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    await store.delete(key);
  } catch (error) {
    console.warn('Erreur lors de la suppression du cache:', error);
  }
};

/**
 * Remplacer entièrement le contenu d'un store (clear + write atomique)
 */
export const replaceStore = async (storeName, items, ttl = 3600000) => {
  if (!isIndexedDBAvailable()) {
    return;
  }

  try {
    const db = await openDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    await store.clear();

    const list = Array.isArray(items) ? items : [];
    await Promise.all(
      list.map((item) => {
        if (!item?.id) return Promise.resolve();
        const cacheEntry = {
          id: item.id,
          data: item,
          cachedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + ttl).toISOString()
        };
        return store.put(cacheEntry);
      })
    );
  } catch (error) {
    console.warn('Erreur lors du remplacement du cache:', error);
  }
};

/**
 * Vider un store
 */
export const clearStore = async (storeName) => {
  if (!isIndexedDBAvailable()) {
    return;
  }

  try {
    const db = await openDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    await store.clear();
  } catch (error) {
    console.warn('Erreur lors du vidage du cache:', error);
  }
};

/**
 * Vider tout le cache
 */
export const clearAllCache = async () => {
  if (!isIndexedDBAvailable()) {
    return;
  }

  try {
    const db = await openDB();
    const transaction = db.transaction(Object.values(STORES), 'readwrite');
    
    await Promise.all(
      Object.values(STORES).map(storeName => {
        const store = transaction.objectStore(storeName);
        return store.clear();
      })
    );
  } catch (error) {
    console.warn('Erreur lors du vidage complet du cache:', error);
  }
};

/**
 * Obtenir la taille du cache (approximative)
 */
export const getCacheSize = async () => {
  if (!isIndexedDBAvailable()) {
    return 0;
  }

  try {
    const db = await openDB();
    let totalSize = 0;

    for (const storeName of Object.values(STORES)) {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      const itemsSize = await new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const items = request.result;
          // Estimation approximative de la taille
          resolve(JSON.stringify(items).length);
        };
        request.onerror = () => reject(request.error);
      });

      totalSize += itemsSize;
    }

    return totalSize;
  } catch (error) {
    console.warn('Erreur lors du calcul de la taille du cache:', error);
    return 0;
  }
};

/**
 * Nettoyer les entrées expirées
 */
export const cleanExpiredCache = async () => {
  if (!isIndexedDBAvailable()) {
    return;
  }

  try {
    const db = await openDB();
    const now = new Date();

    for (const storeName of Object.values(STORES)) {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.openCursor();

      await new Promise((resolve, reject) => {
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const item = cursor.value;
            if (item.expiresAt && new Date(item.expiresAt) <= now) {
              cursor.delete();
            }
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });
    }
  } catch (error) {
    console.warn('Erreur lors du nettoyage du cache:', error);
  }
};

// Nettoyer le cache expiré au démarrage
if (isIndexedDBAvailable()) {
  cleanExpiredCache();
}

/**
 * Invalider le cache (alias pour removeFromCache ou clearStore)
 */
export const invalidateCache = async (storeName, key = 'all') => {
  if (key === 'all') {
    return clearStore(storeName);
  } else {
    return removeFromCache(storeName, key);
  }
};

const cacheUtils = {
  STORES,
  isIndexedDBAvailable,
  getFromCache,
  setToCache,
  setMultipleToCache,
  getAllFromCache,
  removeFromCache,
  clearStore,
  replaceStore,
  clearAllCache,
  getCacheSize,
  cleanExpiredCache,
  invalidateCache
};

export default cacheUtils;